/**
 * Product Query Parameters Handler
 * 
 * Manages URL query parameters for product variant selection and addons.
 * Persists selections across page refreshes.
 * 
 * Query Parameters:
 * - variant: Variant ID (Shopify standard parameter)
 * - variantId: Variant ID (fallback for backward compatibility)
 * - color: Color (option 1)
 * - size: Size (option 2)
 * - handlebar: Handlebar type (option 3)
 * - addons: Comma-separated list of addon product IDs
 */

class ProductQueryParams extends HTMLElement {
  constructor() {
    super();
    this.params = new URLSearchParams(window.location.search);
    this.sectionId = this.dataset.section;
    this.productId = this.dataset.productId;
    this.variantChangeUnsubscriber = null;
    this.observer = null;
    
    this.init();
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Initialize query parameter handling
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setup();
      });
    } else {
      // Small delay to ensure other scripts have loaded
      setTimeout(() => this.setup(), 100);
    }
  }

  /**
   * Setup event listeners and restore state from query parameters
   */
  setup() {
    // Wait for components to be ready
    this.waitForComponents().then(() => {
      // Listen for variant changes FIRST (before restoring)
      this.setupVariantListeners();
      
      // Restore variant selection from query parameters
      this.restoreVariantSelection();
      
      // Restore addons from query parameters (with delay to ensure variant selection is complete)
      setTimeout(() => {
        this.restoreAddons();
      }, 300);
      
      // Listen for addon changes
      this.setupAddonListeners();
    });
  }

  /**
   * Wait for required components to be ready
   */
  waitForComponents() {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max
      
      const checkComponents = () => {
        attempts++;
        
        // Check for radio inputs in bundle (they have names like "option-0-8782168097061")
        const radioInputs = document.querySelectorAll('input[type="radio"][name^="option-"]');
        
        // We need radio inputs to work
        if (radioInputs.length > 0) {
          resolve();
        } else if (attempts >= maxAttempts) {
          resolve(); // Resolve anyway to continue
        } else {
          setTimeout(checkComponents, 100);
        }
      };
      
      checkComponents();
    });
  }

  /**
   * Restore variant selection from query parameters
   */
  restoreVariantSelection() {
    // Try to restore by variant ID (Shopify standard 'variant' first, then fallbacks)
    const variantId = this.params.get('variant') || this.params.get('variantId') || this.params.get('vid');
    if (variantId) {
      this.selectVariantById(variantId);
      return;
    }

    // Otherwise restore by individual options
    const color = this.params.get('color');
    const size = this.params.get('size');
    const handlebar = this.params.get('handlebar');

    if (color) {
      this.selectOption(0, decodeURIComponent(color));
    }
    if (size) {
      this.selectOption(1, decodeURIComponent(size));
    }
    if (handlebar) {
      this.selectOption(2, decodeURIComponent(handlebar));
    }
  }

  /**
   * Select variant by ID
   */
  selectVariantById(variantId) {
    // Try to get variant data from window.productVariants (set by bundle)
    const productId = this.productId;
    if (window.productVariants && window.productVariants[productId]) {
      try {
        const variantData = window.productVariants[productId];
        const variant = variantData.find(v => v.id.toString() === variantId.toString());
        
        if (!variant) {
          return;
        }

        // Select each option with a small delay between selections
        if (variant.option1) {
          setTimeout(() => this.selectOption(0, variant.option1), 50);
        }
        if (variant.option2) {
          setTimeout(() => this.selectOption(1, variant.option2), 100);
        }
        if (variant.option3) {
          setTimeout(() => this.selectOption(2, variant.option3), 150);
        }
      } catch (error) {
        // Silently handle errors
      }
    }
  }

  /**
   * Select an option by position and value
   */
  selectOption(position, value) {
    // Find radio inputs by name pattern (e.g., "option-0-8782168097061")
    const productId = this.productId;
    const optionName = `option-${position}-${productId}`;
    const inputs = document.querySelectorAll(`input[type="radio"][name="${optionName}"]`);
    
    const matchingInput = Array.from(inputs).find(input => {
      const inputValue = input.value.trim();
      const searchValue = value.trim();
      
      // Handle partial matches (e.g., "Bullhorn Bar (+ $199)" matches "Bullhorn Bar")
      return inputValue === searchValue || 
             inputValue.includes(searchValue) || 
             searchValue.includes(inputValue.split('(')[0].trim());
    });

    if (matchingInput && !matchingInput.disabled && !matchingInput.classList.contains('disabled')) {
      matchingInput.checked = true;
      matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Restore addons from query parameters
   */
  restoreAddons() {
    const addonsParam = this.params.get('addons');
    if (!addonsParam) return;

    const addonIds = addonsParam.split(',').filter(id => id.trim());
    const productBundle = document.querySelector('product-bundle');
    if (!productBundle) return;

    // Restore addons with a delay to ensure they're applied in order
    addonIds.forEach((addonId, index) => {
      setTimeout(() => {
        const addonItem = productBundle.querySelector(`[data-bundle-product-item-id="${addonId.trim()}"]`);
        if (addonItem && !addonItem.classList.contains('isChecked')) {
          const checkbox = addonItem.querySelector('[data-bundle-checkbox]');
          if (checkbox) {
            checkbox.click();
          }
        }
      }, index * 100);
    });
  }

  /**
   * Setup listeners for variant changes
   */
  setupVariantListeners() {
    const productId = this.productId;
    
    // Listen directly to ALL radio inputs - this is the most reliable method
    const allRadios = document.querySelectorAll(`input[type="radio"][name^="option-"][name$="-${productId}"]`);
    
    allRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        // Use a timeout to ensure variant is updated
        setTimeout(() => {
          this.updateVariantParams();
        }, 200);
      });
    });

    // Don't listen to swatch clicks directly - let them trigger the radio change event
    // The radio change event will handle the update

    // Listen to bundle variant changes
    const productBundle = document.querySelector('product-bundle');
    if (productBundle) {
      productBundle.addEventListener('change', () => {
        setTimeout(() => {
          this.updateVariantParams();
        }, 200);
      });
    }
  }

  /**
   * Setup listeners for addon changes
   */
  setupAddonListeners() {
    const productBundle = document.querySelector('product-bundle');
    if (!productBundle) return;

    // Listen for addon checkbox changes
    const checkboxes = productBundle.querySelectorAll('[data-bundle-checkbox]');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('click', () => {
        setTimeout(() => this.updateAddonParams(), 100);
      });
    });
  }

  /**
   * Update variant query parameters in URL
   */
  updateVariantParams() {
    // Refresh params from current URL
    this.params = new URLSearchParams(window.location.search);

    const productId = this.productId;
    
    // Get values from selected radio inputs
    const allRadios = document.querySelectorAll(`input[type="radio"][name^="option-"][name$="-${productId}"]:checked`);
    
    // Group by option name to determine position
    const optionInputs = {};
    allRadios.forEach(radio => {
      const name = radio.name;
      // Extract option index from name like "option-0-8782168097061"
      const match = name.match(/option-(\d+)-/);
      if (match) {
        const index = parseInt(match[1]);
        optionInputs[index] = radio.value.trim();
      }
    });

    // Try to find matching variant ID from window.productVariants
    let variantId = null;
    if (window.productVariants && window.productVariants[productId]) {
      const variantData = window.productVariants[productId];
      const variant = variantData.find(v => {
        return v.option1 === optionInputs[0] &&
               v.option2 === optionInputs[1] &&
               v.option3 === optionInputs[2];
      });
      
      if (variant) {
        variantId = variant.id.toString();
        this.params.set('variant', variantId); // Use Shopify standard 'variant' parameter
      }
    }
    
    // Set parameters based on option index
    if (optionInputs[0]) {
      this.params.set('color', encodeURIComponent(optionInputs[0]));
    } else {
      this.params.delete('color');
    }
    if (optionInputs[1]) {
      this.params.set('size', encodeURIComponent(optionInputs[1]));
    } else {
      this.params.delete('size');
    }
    if (optionInputs[2]) {
      this.params.set('handlebar', encodeURIComponent(optionInputs[2]));
    } else {
      this.params.delete('handlebar');
    }

    // Preserve addons if they exist
    const existingAddons = new URLSearchParams(window.location.search).get('addons');
    if (existingAddons) {
      this.params.set('addons', existingAddons);
    }

    this.updateURL();
  }

  /**
   * Update addon query parameters in URL
   */
  updateAddonParams() {
    const productBundle = document.querySelector('product-bundle');
    if (!productBundle) return;

    const checkedItems = productBundle.querySelectorAll('.bundle-product-item.isChecked:not(:first-child)');
    const addonIds = Array.from(checkedItems).map(item => {
      return item.getAttribute('data-bundle-product-item-id');
    }).filter(id => id);

    if (addonIds.length > 0) {
      this.params.set('addons', addonIds.join(','));
    } else {
      this.params.delete('addons');
    }

    this.updateURL();
  }

  /**
   * Update URL without page reload
   */
  updateURL() {
    const queryString = this.params.toString();
    const newURL = `${window.location.pathname}${queryString ? '?' + queryString : ''}`;
    
    // Only update if URL actually changed
    if (window.location.href !== newURL) {
      window.history.replaceState({}, '', newURL);
    }
  }

  /**
   * Get current query parameters as object
   */
  getParams() {
    return {
      variant: this.params.get('variant') || this.params.get('variantId'), // Shopify standard first
      variantId: this.params.get('variantId') || this.params.get('variant'), // Fallback
      color: this.params.get('color'),
      size: this.params.get('size'),
      handlebar: this.params.get('handlebar'),
      addons: this.params.get('addons')?.split(',').filter(id => id) || []
    };
  }
}

customElements.define('product-query-params', ProductQueryParams);
